import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import colorsStore from "shared/store/colorsStore";
import type { ProductCardArrayDateProductReturnCategoryReturnType } from "types/AssistantTypes";
import styles from "./rendimenti.module.scss";
import { formatLocalizedDate } from "utils/dateUtils";

type RendimentiChartProps = {
  data: ProductCardArrayDateProductReturnCategoryReturnType[];
  isin: string;
};

const RendimentiChart: React.FC<RendimentiChartProps> = ({ data, isin }) => {
  const { t, i18n } = useTranslation();

  const formattedData = useMemo(() => {
    return data.map((item) => {
      const dateObj = new Date(item.date);
      const formattedDate = formatLocalizedDate(
        dateObj,
        i18n.language,
        "MMM yyyy",
      );

      return {
        ...item,
        originalDate: item.date,
        date: formattedDate,
        product_return: parseFloat(item.product_return),
        category_return: parseFloat(item.category_return),
        tooltipDate: formatLocalizedDate(dateObj, i18n.language, "dd MMM yyyy"),
      };
    });
  }, [data, i18n.language]);

  const filteredData = formattedData;

  const colorsConfig = colorsStore((state) => state.colorsConfigStore);
  const chartCopy = {
    fontSize: 11,
    fill: colorsConfig.finecoBlack,
    fontFamily: "Proxima Nova",
  };

  const { minReturn, maxReturn, tickStep, ticks, returnDiff } = useMemo(() => {
    const allReturns = [
      ...data.map((d) => parseFloat(d.product_return)),
      ...data.map((d) => parseFloat(d.category_return)),
    ];
    const min = Math.floor(Math.min(...allReturns) * 0.95);
    const max = Math.ceil(Math.max(...allReturns) * 1.05);
    const diff = max - min;
    const step = diff < 40 ? 5 : diff < 60 ? 10 : 50;
    const tickList = Array.from(
      { length: Math.ceil(diff / step) + 1 },
      (_, i) => Math.floor(min / step) * step + i * step,
    );
    const returnDiff = max - min;
    return {
      minReturn: min,
      maxReturn: max,
      tickStep: step,
      ticks: tickList,
      returnDiff,
    };
  }, [data]);

  if (data.length === 0) {
    return null;
  }

  // Custom tooltip with background
  const CustomTooltip = ({ active, payload }: any) => {
    if (active && payload && payload.length) {
      const formattedDate = payload[0].payload.tooltipDate;
      return (
        <div className={styles.tooltipContainer}>
          <p
            className={styles.tooltipLabel}
            style={{ margin: "0 0 5px 0", fontWeight: "bold" }}
          >
            {formattedDate}
          </p>
          {payload.map((entry: any, index: number) => (
            <p
              key={`value-${index}`}
              style={{
                color: entry.color,
                margin: "3px 0",
                fontSize: "12px",
              }}
            >
              {entry.name}: {entry.value.toFixed(2)}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  // Determine appropriate interval for X-axis based on data length
  const getXAxisInterval = (dataLength: number) => {
    if (dataLength > 120) return Math.ceil(dataLength / 12); // Show approximately 12 labels max
    if (dataLength > 60) return 12; // Annual for 5+ years of data
    if (dataLength > 24) return 6; // Semi-annual for 2+ years
    if (dataLength > 12) return 3; // Quarterly for 1+ year
    return 1; // Monthly for less than a year
  };

  return (
    <div className={styles.sectionContainer}>
      <h2 className={styles.sectionTitle}>
        {t("productCard.rendimentiChartTitle")}
      </h2>
      <div className={styles.chartContainer}>
        <ResponsiveContainer debounce={500}>
          <LineChart
            data={filteredData}
            margin={{ top: 10, right: 10, left: -25, bottom: 16 }}
          >
            <CartesianGrid
              strokeWidth={1}
              stroke={colorsConfig.GrayLight2}
              strokeOpacity={1}
              horizontal={true}
              vertical={false}
            />
            <XAxis
              dataKey="date"
              dx={40}
              dy={5}
              angle={-20}
              textAnchor="end"
              height={40}
              interval={getXAxisInterval(filteredData.length)}
              tick={chartCopy}
              tickLine={false}
              axisLine={{ stroke: colorsConfig.GrayLight2 }}
            />
            <YAxis
              tick={chartCopy}
              tickCount={Math.ceil(returnDiff / tickStep)}
              domain={[minReturn, maxReturn]}
              tickLine={false}
              tickFormatter={(value) => Math.round(value).toString()}
              ticks={ticks}
              axisLine={{ stroke: colorsConfig.GrayLight2 }}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend content={<CustomizedLegend />} />
            <Line
              name={`Rendimenti ${isin}`}
              type="monotone"
              dataKey="product_return"
              stroke={colorsConfig.productCard1}
              dot={false}
              strokeWidth={1.5}
              strokeOpacity={1}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
            <Line
              name="Rendimento Categoria"
              type="monotone"
              dataKey="category_return"
              stroke={colorsConfig.productCard2}
              dot={false}
              strokeWidth={1.5}
              strokeOpacity={1}
              activeDot={{ r: 5 }}
              isAnimationActive={false}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const CustomizedLegend = (props: any) => {
  const { payload } = props;
  return (
    <div className={styles.legendContainer}>
      {payload.map((entry: any, index: number) => (
        <div key={`item-${index}`} className={styles.legendItem}>
          <div
            className={styles.legendDot}
            style={{ backgroundColor: entry.color }}
          />
          <span className={styles.legendCopy}>{entry.value}</span>
        </div>
      ))}
    </div>
  );
};

export default RendimentiChart;
